import React, { Component } from "react";
import { Link, navigate } from "@reach/router";
import { Formik } from "formik";
import styled from "styled-components";
import { themeGet } from "styled-system";
import { syncStore } from "../../state/appStore";
import { currentLocale } from "../../services/locale";
import { createAccount, authenticate } from "../../services/moocfi";
import { createMailchimpSubscriber } from "../../services/zapier";
import * as routes from "../../constants/routes";
import nickGenerator from "../../utilities/nick-generator";
import { loggedIn } from '../../services/moocfi';

import { injectIntl, FormattedMessage } from "react-intl";

import StyledForm from "../StyledForm";
import { SmallContainer } from "../Grid";
import ExtraInfoForm from "../ExtraInfo";
import TextF2 from "../TextF2";
import TextF7 from "../TextF7";
import TextF8 from "../TextF8";
import CountrySelect from "./CountrySelect";

export const StyledF2 = styled(TextF2)`
  text-align: center;
  color: white;
`;

export const StyledF7 = styled(TextF7)`
  text-align: center;
  color: white;
`;

export const Error = styled.div`
  color: #ff7351;
`;

export const ErrorMessage = styled.div`
  color: #ff7351;
  text-align: center;
  margin-top: 1.5rem;
`;

export const SubmitButton = styled.button`
  font-size: 1rem;
  outline: none !important;
  font-family: ${themeGet("fonts.TTNormsProBold")};
  color: ${props =>
    props.disabled ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,1)"};
  max-width: 280px;
  padding: 1rem;
  line-height: 2rem;
  border-radius: 2rem;
  background: #32c2a2;
  border: none;
  width: ${props => (props.disabled ? "80%" : "100%")};
  opacity: ${props => (props.disabled ? "0.5" : "1")};
  transition: all 0.2s ease;
  margin: 0 auto;
  display: block;
  box-shadow: 0 0 4px 3px rgba(0, 0, 0, 0.04);
  :hover {
    cursor: pointer;
  }
`;

const updateByPropertyName = (propertyName, value) => () => ({
  [propertyName]: value
});

const mapLocaleToDefaultCountry = {
  en: "United Kingdom",
  de: "Germany",
  fi: "Finland",
  se: "Sweden",
  lv: "Latvia",
  no: "Norway",
  ee: "Estonia",
  mt: "Malta",
  fr: "France",
  lt: "Lithuania",
  "nl-be": "Belgium",
  "fr-be": "Belgium",
  "en-ie": "Ireland",
  it: "Italy",
  pl: "Poland",
  "en-lu": "Luxembourg",
  cs: "Czech Republic",
  hr: "Croatia",
  da: "Denmark",
  ro: "Romania",
  is: "Iceland",
  sk: "Slovakia",
  bg: "Bulgaria",
  sl: "Slovenia",
  "de-at": "Austria",
  nl: "Netherlands",
  el: "Greece",
  es: "Spain",
  pt: "Portugal",
  default: "United Kingdom"
};

export const INITIAL_STATE = {
  username:
    nickGenerator()
      .toLowerCase()
      .replace(/\s/g, "-") +
    "-" +
    Math.floor(Math.random() * 1000000),
  email: "",
  password: "",
  password_confirmation: "",
  first_name: "",
  last_name: "",
  postal_code: "",
  country: mapLocaleToDefaultCountry[currentLocale()],
  studentNumber: "",
  deadline: "self-paced",
  helsinki_university: "",
  open_university: "",
  research: "",
  marketing: "",
  accept_policy_versions: [],
  language: currentLocale(),
  error: null,
  complete: false,
  redirect: null
};

class SignUpForm extends Component {
  constructor(props) {
    super(props);
    this.state = { ...INITIAL_STATE };
  }

  componentDidMount() {
    if (loggedIn()) {
      const defaultPath =
        currentLocale() === "en" ? "/" : `/${currentLocale()}`;
      let passedReturnPath = null;

      if (typeof window !== "undefined") {
        const urlParams = new URLSearchParams(window.location.search);
        const returnTo = urlParams.get("return_to");
        if (returnTo && returnTo.indexOf("/") === 0) {
          passedReturnPath = returnTo;
        }
      }

      this.setState({
        redirect:
          passedReturnPath ||
          this.props.location.search.substring(1) ||
          defaultPath
      });
    }
  }

  handleSubmit = values => {
    const data = {
      username: values.username,
      email: values.email,
      password: values.password,
      password_confirmation: values.password_confirmation,
      email_confirmation: values.email
    };

    const extraData = {
      deadline: "self-paced",
      marketing: values.marketing,
      research: values.research,
      helsinki_university: values.helsinki_university,
      open_university: values.open_university,
      language: values.language,
      postal_code: values.postal_code,
      country: values.country,
      accept_policy_versions: values.accept_policy_versions ? [process.env.PRIVACY_VERSION] : []
    };

    const userField = {
      organizational_id: values.studentNumber,
      first_name: values.first_name,
      last_name: values.last_name
    };

    return new Promise((resolve, reject) => {
      createAccount(data, extraData, userField)
        .then(res => {
          authenticate({
            username: values.email,
            password: values.password
          })
            .then(() => syncStore())
            .then(() => {
              // Send user to Zapier -> Mailchimp
              createMailchimpSubscriber(values);
              this.setState({
                complete: true
              });
              resolve(res);
            });
        })
        .catch(error => {
          this.setState(updateByPropertyName("error", JSON.stringify(error)));
          reject(error);
        });
    });
  };

  render() {

    if (this.state.redirect) {
      navigate(this.state.redirect);
      return null;
    }

    if (this.state.complete) {
      const defaultPath =
        currentLocale() === "en" ? "/" : `/${currentLocale()}`;
      const redirectUrl =
        this.props.location.search.substring(1) || defaultPath;

      navigate(`${redirectUrl}`);
      return null;
    }

    const validate = values => {
      let errors = {};
      if (!values.email) {
        errors.email = this.props.intl.formatMessage({
          id: "pwChange.required"
        });
      } else if (
        !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(values.email)
      ) {
        errors.email = this.props.intl.formatMessage({
          id: "userDetails.invalidEmail"
        });
      }

      if (!values.password && !values.password_confirmation) {
        errors.password = this.props.intl.formatMessage({
          id: "pwChange.required"
        });
        errors.password_confirmation = this.props.intl.formatMessage({
          id: "pwChange.required"
        });
      } else if (
        values.password_confirmation &&
        values.password !== values.password_confirmation
      ) {
        errors.password = this.props.intl.formatMessage({
          id: "pwChange.pwMustMatch"
        });
        errors.password_confirmation = this.props.intl.formatMessage({
          id: "pwChange.pwMustMatch"
        });
      }

      if (!values.first_name) {
        errors.first_name = this.props.intl.formatMessage({
          id: "pwChange.required"
        });
      }

      if (!values.last_name) {
        errors.last_name = this.props.intl.formatMessage({
          id: "pwChange.required"
        });
      }

      if (!values.country) {
        errors.country = this.props.intl.formatMessage({
          id: "pwChange.required"
        });
      }

      if (!values.postal_code) {
        errors.postal_code = this.props.intl.formatMessage({
          id: "pwChange.required"
        });
      }

      if (!values.accept_policy) {
        errors.form = this.props.intl.formatMessage({
          id: "signUp.error"
        });
      }

      if (Object.keys(errors).length) {
        errors.form = this.props.intl.formatMessage({
          id: "signUp.error"
        });
      }

      return errors;
    };
    return (
      <div>
        <Formik
          initialValues={INITIAL_STATE}
          validate={validate}
          onSubmit={(values, { setSubmitting, setErrors }) => {
            setSubmitting(true);
            this.handleSubmit(values).then(
              res => {
                setSubmitting(false);
              },
              errors => {
                setSubmitting(false);
                setErrors(errors);
              }
            );
          }}
          render={({
            values,
            errors,
            isValid,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting
          }) => (
            <StyledForm>
              <form onSubmit={handleSubmit}>
                <SmallContainer pt={6}>
                  <StyledF2 color="#fff">
                    <FormattedMessage id="globals.signUp" />
                  </StyledF2>
                  <StyledF7>
                    <FormattedMessage id="signUp.description" />
                  </StyledF7>
                  {currentLocale() === "it" &&
                    <StyledF7>
                      <FormattedMessage id="ects.text" />
                    </StyledF7>}
                  <fieldset>
                    <label htmlFor="email">
                      <FormattedMessage id="signUp.email" />
                    </label>
                    <input

                      type="email"
                      name="email"
                      id="email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                    />
                    {touched.email && errors.email && (
                      <Error>{errors.email}</Error>
                    )}
                    <label htmlFor="password">
                      <FormattedMessage id="signUp.Password" />
                    </label>
                    <input

                      type="password"
                      name="password"
                      id="password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.password}
                    />
                    {touched.password && errors.password && (
                      <Error>{errors.password}</Error>
                    )}
                    <label htmlFor="password_confirmation">
                      <FormattedMessage id="signUp.confirmPassword" />
                    </label>
                    <input

                      type="password"
                      name="password_confirmation"
                      id="password_confirmation"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.password_confirmation}
                    />
                    {touched.password_confirmation &&
                      errors.password_confirmation && (
                        <Error>{errors.password_confirmation}</Error>
                      )}
                    <label htmlFor="first_name">
                      <FormattedMessage id="signUp.firstName" />
                    </label>
                    <input

                      type="text"
                      name="first_name"
                      id="first_name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.first_name}
                    />
                    {touched.first_name && errors.first_name && (
                      <Error>{errors.first_name}</Error>
                    )}
                    <label htmlFor="last_name">
                      <FormattedMessage id="signUp.lastName" />
                    </label>
                    <input

                      type="text"
                      name="last_name"
                      id="last_name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.last_name}
                    />
                    {touched.last_name && errors.last_name && (
                      <Error>{errors.last_name}</Error>
                    )}
                    {values.country === "Finland" && (
                      <React.Fragment>
                        <label htmlFor="studentNumber">
                          <FormattedMessage id="signUp.studentNumber" />
                        </label>
                        <input
                          type="text"
                          name="studentNumber"
                          id="studentNumber"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.studentNumber}
                        />
                        {touched.studentNumber && errors.studentNumber && (
                          <Error>{errors.studentNumber}</Error>
                        )}
                      </React.Fragment>
                    )}

                    <label htmlFor="country">
                      <FormattedMessage id="signUp.country" />
                    </label>
                    <select

                      name="country"
                      id="country"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.country}
                    >
                      <CountrySelect />
                    </select>
                    {errors.country && <Error>{errors.country}</Error>}

                    <label htmlFor="postal_code">
                      <FormattedMessage id="signUp.postalCode" />
                    </label>
                    <input
                      type="text"
                      name="postal_code"
                      id="postal_code"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.postal_code}
                    />

                    <input
                      type="hidden"
                      name="language"
                      id="language"
                      value={values.language}
                    />
                    {touched.postal_code && errors.postal_code && (
                      <Error>{errors.postal_code}</Error>
                    )}
                  </fieldset>
                </SmallContainer>

                <ExtraInfoForm
                  values={values}
                  touched={touched}
                  errors={errors}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  handleSubmit={handleSubmit}
                  intl={this.props.intl}
                />

                <TextF8 textAlign="center" mt={0}>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={this.props.intl.formatMessage({
                      id: "routes.PRIVACY"
                    })}
                  >
                    <FormattedMessage id="signUp.privacyLink" />
                  </a>
                </TextF8>
                <TextF8 textAlign="center" mt={0}>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={this.props.intl.formatMessage({
                      id: "routes.TERMS"
                    })}
                  >
                    <FormattedMessage id="globals.menu.termsofService" />
                  </a>
                </TextF8>
                <SubmitButton

                  type="submit"
                  disabled={isSubmitting || !isValid}
                >
                  {isSubmitting
                    ? this.props.intl.formatMessage({
                      id: "globals.submitting"
                    })
                    : this.props.intl.formatMessage({ id: "globals.submit" })}
                </SubmitButton>
                {touched && Object.keys(errors).length > 0 && (
                  <ErrorMessage>
                    <FormattedMessage id="signUp.error" />
                  </ErrorMessage>
                )}
              </form>
            </StyledForm>
          )}
        />
      </div>
    );
  }
}

export const SignUpLink = () => (
  <p>
    Don't have an account? <Link to={routes.SIGN_UP}>Sign Up</Link>
  </p>
);

export default injectIntl(SignUpForm);
